import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Radar, Loader2, RefreshCw, Filter } from "lucide-react";
import TopBar from "@/components/TopBar";
import TickerSearchDialog from "@/components/TickerSearchDialog";
import DisclaimerFooter from "@/components/DisclaimerFooter";
import { ValidationBanner, useValidationStatus } from "@/components/Validation";
import { api } from "@/lib/api";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "strong_long", label: "Strong LONG (>=80)" },
  { key: "strong_short", label: "Strong SHORT (>=80)" },
  { key: "new_signal", label: "New Signal" },
  { key: "high_momentum", label: "High Momentum" },
  { key: "unusual_volume", label: "Unusual Volume" },
  { key: "high_rs", label: "High Rel Strength" },
  { key: "breakout", label: "Breakout" },
];

function fmt(v, d = 2) { return v == null ? "—" : Number(v).toFixed(d); }

function SignalChip({ s }) {
  const cls = s === "LONG" ? "text-emerald-300 border-emerald-500/40 bg-emerald-500/10" :
              s === "SHORT" ? "text-red-300 border-red-500/40 bg-red-500/10" :
              "text-slate-400 border-slate-600 bg-slate-500/5";
  return <span className={`mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 border ${cls}`}>{s}</span>;
}

function ConfChip({ tier, descriptive }) {
  if (descriptive) {
    // Swing verdict is MARGINAL/NULL — do not present a confidence tier
    return (
      <span
        className="mono text-[10px] uppercase tracking-widest text-slate-500"
        title="Not a validated confidence — see Analytics for validation status."
      >
        desc
      </span>
    );
  }
  const cls = tier === "high" ? "text-emerald-300" : tier === "medium" ? "text-cyan-300" : "text-slate-400";
  return <span className={`mono text-[10px] uppercase tracking-widest ${cls}`}>{tier || "—"}</span>;
}

export default function ScannerPage() {
  const [openSearch, setOpenSearch] = useState(false);
  const validation = useValidationStatus();
  const swingDescriptive = validation?.swing?.verdict === "MARGINAL" || validation?.swing?.verdict === "NULL";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [sortKey, setSortKey] = useState("score");
  const [sortDir, setSortDir] = useState("desc");
  const nav = useNavigate();

  const load = async (refresh = false) => {
    setLoading(true);
    try {
      const r = await api.get("/signal/scanner", { params: refresh ? { refresh: true } : {} });
      setData(r.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(false); }, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    let rows = data.rows || [];
    switch (filter) {
      case "strong_long": rows = rows.filter((r) => r.signal === "LONG" && (r.long_score || 0) >= 80); break;
      case "strong_short": rows = rows.filter((r) => r.signal === "SHORT" && (r.short_score || 0) >= 80); break;
      case "new_signal": rows = rows.filter((r) => r.signal_changed); break;
      case "high_momentum": rows = rows.filter((r) => (r.momentum || 0) >= 70); break;
      case "unusual_volume": rows = rows.filter((r) => (r.rvol || 0) >= 1.5); break;
      case "high_rs": rows = rows.filter((r) => (r.rs || 0) >= 65); break;
      case "breakout": rows = rows.filter((r) => r.signal === "LONG" && (r.long_score || 0) >= 60 && (r.rvol || 0) >= 1.2); break;
      default: break;
    }
    const keyFn = {
      score: (r) => Math.max(r.long_score || 0, r.short_score || 0),
      ticker: (r) => r.ticker,
      price: (r) => r.price || 0,
      trend: (r) => r.trend || 0,
      momentum: (r) => r.momentum || 0,
      rvol: (r) => r.rvol || 0,
      rs: (r) => r.rs || 0,
      rr: (r) => r.risk_reward_t1 || 0,
    }[sortKey] || ((r) => 0);
    rows = [...rows].sort((a, b) => {
      const av = keyFn(a); const bv = keyFn(b);
      if (av === bv) return 0;
      return (av > bv ? 1 : -1) * (sortDir === "desc" ? -1 : 1);
    });
    return rows;
  }, [data, filter, sortKey, sortDir]);

  const setSort = (k) => {
    if (sortKey === k) setSortDir(sortDir === "desc" ? "asc" : "desc");
    else { setSortKey(k); setSortDir("desc"); }
  };

  const Th = ({ k, children, right = false }) => (
    <th className={right ? "right cursor-pointer select-none" : "cursor-pointer select-none"} onClick={() => setSort(k)}>
      {children}{sortKey === k ? (sortDir === "desc" ? " ▼" : " ▲") : ""}
    </th>
  );

  return (
    <div className="min-h-screen bg-[#080B11] flex flex-col">
      <TopBar onOpenSearch={() => setOpenSearch(true)} />
      <main className="flex-1 pb-14">
        <div className="max-w-[1400px] mx-auto p-3 sm:p-4">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <Radar className="h-4 w-4 text-cyan-400" />
            <h1 className="mono text-lg font-bold text-slate-100 tracking-tight uppercase">Market Scanner</h1>
            {data?.regime && (
              <span className="mono text-[10px] uppercase tracking-widest text-slate-500 ml-2">
                Regime: <span className="text-slate-200">{data.regime.regime}</span> · Universe: {data.universe_size}
              </span>
            )}
            <span data-testid="scanner-count" className="mono text-[10px] uppercase tracking-widest text-slate-500 ml-auto">
              {filtered.length} rows · {data?.cache === "hit" ? "cached" : "fresh"}
            </span>
            <button data-testid="scanner-refresh" onClick={() => load(true)} disabled={loading} className="mono text-[10px] uppercase tracking-widest px-3 py-1.5 border border-cyan-500/40 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-200 disabled:opacity-50 flex items-center gap-1">
              {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
              Refresh scan
            </button>
          </div>

          <div className="border border-[#1E293B] bg-[#0F1626] px-3 py-2 mb-3 flex flex-wrap items-center gap-1" data-testid="scanner-filters">
            <Filter className="h-3.5 w-3.5 text-slate-400 mr-1" />
            {FILTERS.map((f) => (
              <button
                key={f.key}
                data-testid={`filter-${f.key}`}
                onClick={() => setFilter(f.key)}
                className={`mono text-[10px] uppercase tracking-widest px-2 py-1 border ${filter === f.key ? "border-cyan-500/60 bg-cyan-500/15 text-cyan-200" : "border-[#1E293B] text-slate-400 hover:text-slate-200"}`}
              >{f.label}</button>
            ))}
          </div>

          {validation?.swing && (
            <ValidationBanner status={validation.swing} testid="scanner-validation-banner" className="mb-3" />
          )}

          <div className="border border-[#1E293B] bg-[#0F1626] overflow-auto max-h-[calc(100vh-260px)]">
            {loading && !data && (
              <div className="p-8 text-slate-500 mono text-xs text-center flex items-center justify-center gap-2">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Scanning universe…
              </div>
            )}
            {data && filtered.length === 0 && !loading && (
              <div className="p-8 text-slate-500 mono text-xs text-center" data-testid="scanner-empty">
                No rows match this filter.
              </div>
            )}
            {data && filtered.length > 0 && (
              <table className="q-table w-full">
                <thead className="sticky top-0 bg-[#0B111C]">
                  <tr>
                    <Th k="ticker">Ticker</Th>
                    <th>Company</th>
                    <Th k="price" right>Price</Th>
                    <th>Signal</th>
                    <Th k="score" right>Score</Th>
                    <th>Conf</th>
                    <Th k="trend" right>Trend</Th>
                    <Th k="momentum" right>Mom</Th>
                    <Th k="rvol" right>RVOL</Th>
                    <Th k="rs" right>RS</Th>
                    <Th k="rr" right>R/R</Th>
                    <th>Top Factor</th>
                    <th>Sector</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => (
                    <tr
                      key={r.ticker}
                      data-testid={`scanner-row-${r.ticker}`}
                      className="hover:bg-[#151C2C] cursor-pointer"
                      onClick={() => nav(`/stock/${r.ticker}`)}
                    >
                      <td className="text-slate-100 font-bold">
                        {r.ticker}
                        {r.signal_changed && <span className="ml-2 mono text-[9px] uppercase tracking-widest px-1 py-0.5 border border-cyan-500/60 text-cyan-300 bg-cyan-500/10">NEW</span>}
                      </td>
                      <td className="text-slate-500 text-[11px]">{(r.company || "—").slice(0, 24)}</td>
                      <td className="right text-slate-200">{r.price != null ? `$${fmt(r.price)}` : "—"}</td>
                      <td><SignalChip s={r.signal} /></td>
                      <td className={"right " + (r.signal === "LONG" ? "text-emerald-300" : r.signal === "SHORT" ? "text-red-300" : "text-slate-400")}>
                        {r.signal === "SHORT" ? fmt(r.short_score, 1) : fmt(r.long_score, 1)}
                      </td>
                      <td><ConfChip tier={r.confidence} descriptive={swingDescriptive} /></td>
                      <td className="right text-slate-300">{fmt(r.trend, 0)}</td>
                      <td className="right text-slate-300">{fmt(r.momentum, 0)}</td>
                      <td className={"right " + ((r.rvol || 0) >= 1.5 ? "text-emerald-300" : "text-slate-400")}>{r.rvol != null ? fmt(r.rvol, 2) + "x" : "—"}</td>
                      <td className="right text-slate-300">{fmt(r.rs, 0)}</td>
                      <td className="right text-slate-200">{fmt(r.risk_reward_t1, 2)}</td>
                      <td className="text-slate-500 text-[10px]">{r.top_factor || "—"}</td>
                      <td className="text-slate-500 text-[10px]">{r.sector_etf || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </main>
      <TickerSearchDialog open={openSearch} onOpenChange={setOpenSearch} />
      <DisclaimerFooter />
    </div>
  );
}
